import React, { useEffect } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import requireAuth from "@/_common/hoc/reqAuth";
import { useTheme } from "@/hooks/useTheme";
import { GlobalFilterProvider } from "@/context/GlobalFilterContext";

import OverviewContainer from "@/containers/overview";
import RevenueContainer from "@/containers/revenue";
import SubscriptionContainer from "@/containers/subscription";
import CompaniesContainer from "@/containers/companies";
import CompanyDetailsContainer from "@/containers/company-details";
import JobsContainer from "@/containers/jobs";
import HiringFunnelContainer from "@/containers/hiring-funnel";
import AiUsageContainer from "@/containers/ai-usage";
import MeetingsContainer from "@/containers/meetings";
import PerformanceContainer from "@/containers/performance";
import FeedbackContainer from "@/containers/feedback";
import UsersContainer from "@/containers/users";
import SettingsContainer from "@/containers/settings";
import PlatformSettingsContainer from "@/containers/platform-settings";
import NotFoundContainer from "@/containers/not-found";
import LoginPage from "@/pages/login";
import VerifyOtpPage from "@/pages/verify-otp";

// Wrap each protected container once at module scope.
// If these were created inline inside Router(), every re-render of Router
// would produce a new component type, causing React to unmount/remount
// the current page and fire its useEffect hooks a second time.
const AuthOverview = requireAuth(OverviewContainer);
const AuthRevenue = requireAuth(RevenueContainer);
const AuthSubscription = requireAuth(SubscriptionContainer);
const AuthCompanies = requireAuth(CompaniesContainer);
const AuthCompanyDetails = requireAuth(CompanyDetailsContainer);
const AuthJobs = requireAuth(JobsContainer);
const AuthHiringFunnel = requireAuth(HiringFunnelContainer);
const AuthAiUsage = requireAuth(AiUsageContainer);
const AuthMeetings = requireAuth(MeetingsContainer);
const AuthPerformance = requireAuth(PerformanceContainer);
const AuthFeedback = requireAuth(FeedbackContainer);
const AuthUsers = requireAuth(UsersContainer);
const AuthSettings = requireAuth(SettingsContainer);
const AuthPlatformSettings = requireAuth(PlatformSettingsContainer);

function ThemeInitializer() {
  const { theme } = useTheme();
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <Switch>
        {/* Public routes — no auth required */}
        <Route exact path="/login" component={LoginPage} />
        <Route exact path="/verify-otp" component={VerifyOtpPage} />

        {/* Protected routes — redirect to /login if not authenticated */}
        <Route exact path="/" component={AuthOverview} />
        <Route exact path="/revenue" component={AuthRevenue} />
        <Route exact path="/subscription" component={AuthSubscription} />
        <Route exact path="/companies" component={AuthCompanies} />
        <Route exact path="/companies/:id" component={AuthCompanyDetails} />
        <Route exact path="/jobs" component={AuthJobs} />
        <Route exact path="/hiring-funnel" component={AuthHiringFunnel} />
        <Route exact path="/ai-usage" component={AuthAiUsage} />
        <Route exact path="/meetings" component={AuthMeetings} />
        <Route exact path="/performance" component={AuthPerformance} />
        <Route exact path="/feedback" component={AuthFeedback} />
        <Route exact path="/users" component={AuthUsers} />
        <Route exact path="/settings" component={AuthSettings} />
        <Route exact path="/platform-settings" component={AuthPlatformSettings} />
        <Route component={NotFoundContainer} />
      </Switch>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <GlobalFilterProvider>
          <ThemeInitializer />
          <Toaster />
          <SonnerToaster />
          <Router />
        </GlobalFilterProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
